import { Suspense, lazy, useState } from "react";
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Performance wrapper for the assistant.
 *
 * The full assistant pulls in the AI SDK + streaming markdown renderer
 * (code highlighting, math, diagrams), which is by far the largest
 * dependency graph reachable from the public marketing shell. Loading it
 * eagerly put ~1 MB of JavaScript on the homepage's initial download even
 * though the assistant is closed until a visitor asks for it.
 *
 * This component renders only the floating launcher (visually identical to
 * the widget's own launcher) and dynamically imports the real widget the
 * first time it is clicked. No assistant behaviour changes.
 */
const AssistantWidget = lazy(() =>
  import("./AssistantWidget").then((m) => ({ default: m.AssistantWidget })),
);

const PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID;

export function DeferredAssistantWidget({ context }: { context: "public" | "app" }) {
  const [activated, setActivated] = useState(false);

  if (!PROJECT_ID) return null;

  if (activated) {
    return (
      <Suspense fallback={null}>
        <AssistantWidget context={context} defaultOpen />
      </Suspense>
    );
  }

  return (
    <button
      type="button"
      aria-label="Open AI Visibility Assistant"
      onClick={() => setActivated(true)}
      className={cn(
        "fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full",
        "bg-primary text-primary-foreground shadow-[0_8px_30px_rgb(242,160,0,0.35)]",
        "hover:scale-105 transition-all duration-200",
        "flex items-center justify-center",
        "ring-1 ring-primary/30",
      )}
    >
      <Sparkles className="h-5 w-5" />
    </button>
  );
}
